import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styles from './SearchResultsPage.module.css';
import MountainCard from './MountainCard';
import UserSearchResultCard from './UserSearchResultCard';
import GearCard from './GearCard';
import API_BASE_URL from '../config/api';
import { Helmet } from '../utils/helmet';
import { useTranslation } from 'react-i18next';

const createEmptyResults = () => ({ mountains: [], users: [], gear: [], posts: [] });

function SearchResultsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const searchQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return (params.get('q') || '').trim();
  }, [location.search]);

  const [results, setResults] = useState(createEmptyResults);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!searchQuery) {
      setResults(createEmptyResults());
      setError('');
      setLoading(false);
      return;
    }

    let isMounted = true;
    const controller = new AbortController();

    const fetchResults = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/search?q=${encodeURIComponent(searchQuery)}`,
          { signal: controller.signal }
        );
        const payload = await response.json();
        if (!isMounted) return;

        if (response.ok && payload.success) {
          setResults({
            mountains: payload.data?.mountains || [],
            users: payload.data?.users || [],
            gear: payload.data?.gear || [],
            posts: payload.data?.posts || []
          });
        } else {
          setResults(createEmptyResults());
          setError(payload.message || t('searchResults.error'));
        }
      } catch (err) {
        if (!isMounted || err.name === 'AbortError') return;
        setResults(createEmptyResults());
        setError(t('searchResults.network'));
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchResults();
    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [searchQuery, t]);

  const hasResults = Object.values(results).some((list) => list.length > 0);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    if (typeof timestamp.toDate === 'function') {
      return timestamp.toDate().toLocaleString();
    }
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleString();
    }
    if (timestamp._seconds) {
      return new Date(timestamp._seconds * 1000).toLocaleString();
    }
    if (typeof timestamp === 'number') {
      return new Date(timestamp).toLocaleString();
    }
    if (typeof timestamp === 'string') {
      const parsed = new Date(timestamp);
      return Number.isNaN(parsed.getTime()) ? '' : parsed.toLocaleString();
    }
    return '';
  };

  const getPostExcerpt = (text = '') => {
    const cleaned = text.trim();
    if (cleaned.length <= 200) return cleaned;
    return `${cleaned.slice(0, 197)}…`;
  };

  const renderTagPills = (tags = [], variant = 'mountain') => {
    return tags.map((tag) => {
      const label = tag.name || tag.id;
      const to = `/browse?tag=${encodeURIComponent(label)}`;
      return (
        <Link
          key={`${variant}-${label}`}
          to={to}
          className={`${styles.postTag} ${
            variant === 'mountain' ? styles.mountainTag : styles.gearTag
          }`}
        >
          {label}
        </Link>
      );
    });
  };

  const handleBackClick = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/browse');
    }
  };

  const helmetTitle = searchQuery
    ? t('searchResults.helmetTitleQuery', { query: searchQuery })
    : t('searchResults.helmetTitle');

  return (
    <>
      <Helmet>
        <title>{helmetTitle}</title>
      </Helmet>
      <div className={styles.searchResultsPage}>
        <div className={styles.header}>
          <button onClick={handleBackClick} className={`${styles.backButton} backButton`}>
            &larr; {t('searchResults.back')}
          </button>
          <div>
            <h1 className={styles.title}>{t('searchResults.title')}</h1>
            {searchQuery && (
              <p className={styles.query}>{t('searchResults.queryLabel', { query: searchQuery })}</p>
            )}
          </div>
        </div>

        {!searchQuery && (
          <p className={styles.noResults}>{t('searchResults.hint')}</p>
        )}

        {searchQuery && loading && (
          <p className={styles.status}>{t('searchResults.searching')}</p>
        )}

        {searchQuery && !loading && error && (
          <p className={`${styles.status} ${styles.statusError}`}>{error}</p>
        )}

        {searchQuery && !loading && !error && !hasResults && (
          <p className={styles.noResults}>{t('searchResults.empty', { query: searchQuery })}</p>
        )}

        {hasResults && (
          <>
            {results.mountains.length > 0 && (
              <section className={styles.resultsSection}>
                <h2>{t('searchResults.sections.mountains', { count: results.mountains.length })}</h2>
                <div className={styles.mountainsGrid}>
                  {results.mountains.map((mountain) => (
                    <MountainCard key={mountain.id} mountain={mountain} />
                  ))}
                </div>
              </section>
            )}

            {results.users.length > 0 && (
              <section className={styles.resultsSection}>
                <h2>{t('searchResults.sections.users', { count: results.users.length })}</h2>
                <div className={styles.usersGrid}>
                  {results.users.map((user) => (
                    <UserSearchResultCard
                      key={user._id}
                      user={user}
                      onClick={() => navigate(`/profile/${user.username}`)}
                    />
                  ))}
                </div>
              </section>
            )}

{results.gear.length > 0 && (
              <section className={styles.resultsSection}>
                <h2>{t('searchResults.sections.gear', { count: results.gear.length })}</h2>
                <div className={styles.gearGrid}>
                  {results.gear.map((item) => (
                    <GearCard key={item.id} gear={item} />
                  ))}
                </div>
              </section>
            )}

            {results.posts.length > 0 && (
              <section className={styles.resultsSection}>
                <h2>{t('searchResults.sections.posts', { count: results.posts.length })}</h2>
                <div className={styles.postsList}>
                  {results.posts.map((post) => (
                    <article key={post.id} className={styles.postCard}>
                      <div className={styles.postMeta}>
                        <span className={styles.postAuthor}>@{post.username || 'anonymous'}</span>
                        {post.timestamp && (
                          <time className={styles.postTime}>{formatTimestamp(post.timestamp)}</time>
                        )}
                      </div>
                      <p className={styles.postExcerpt}>{getPostExcerpt(post.postText)}</p>
                      {(post.mountainTags?.length > 0 || post.gearTags?.length > 0) && (
                        <div className={styles.postTags}>
                          {renderTagPills(post.mountainTags, 'mountain')}
                          {renderTagPills(post.gearTags, 'gear')}
                        </div>
                      )}
                      <Link to="/community" className={styles.postLink}>
                        {t('searchResults.visitCommunity')}
                      </Link>
                    </article>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </>
  );
}

export default SearchResultsPage;
